import { uploadImage } from '@/api/upload-image';
import IconGrid, { IconGridSection } from '@/components/build/icon-grid';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { fetchBuilderIcons } from '@/api/builder-icons';
import { ImagePlusIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';

interface IconPickerProps {
	value: string | null;
	disabled?: boolean;
	onChange: (url: string | null) => void;
	onUploadingChange?: (uploading: boolean) => void;
}

export default function IconPicker({ value, disabled = false, onChange, onUploadingChange }: IconPickerProps) {
	const fileRef = useRef<HTMLInputElement | null>(null);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [userIcons, setUserIcons] = useState<string[]>([]);
	const [defaultIcons, setDefaultIcons] = useState<string[]>([]);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const data = await fetchBuilderIcons();
				if (!cancelled) {
					setUserIcons(data.userIcons);
					setDefaultIcons(data.defaultIcons);
				}
			} catch {
				if (!cancelled) {
					toast.error('Не удалось загрузить иконки');
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		void load();

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		onUploadingChange?.(uploading);
	}, [uploading, onUploadingChange]);

	useEffect(() => {
		if (value) {
			setPreviewUrl(value);
		} else {
			setPreviewUrl(null);
		}
	}, [value]);

	function handleSelect(url: string) {
		if (disabled) return;
		setPreviewUrl(url);
		onChange(url);
	}

	function handleRemove() {
		if (disabled) return;
		setPreviewUrl(null);
		onChange(null);
	}

	async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
		if (disabled) return;

		const file = e.target.files?.[0];
		if (!file) return;

		try {
			setUploading(true);
			const url = await uploadImage(file, file.name);
			setPreviewUrl(url);
			onChange(url);
		} catch (error) {
			console.error(error);
			toast.error('Ошибка при загрузке файла');
		} finally {
			setUploading(false);
			e.target.value = '';
		}
	}

	return (
		<div className='flex flex-col gap-3'>
			<Label className='text-sm'>Иконка приложения</Label>

			<div className='flex justify-center'>
				<button
					type='button'
					disabled={disabled || uploading}
					className='relative aspect-square w-28 rounded-xl border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-colors hover:bg-muted/50 hover:border-muted-foreground/30 disabled:cursor-not-allowed shadow-sm'
					onClick={() => fileRef.current?.click()}
				>
					{previewUrl ? (
						<>
							<img src={previewUrl} alt='Выбранная иконка' className='absolute inset-0 w-full h-full object-cover rounded-[10px]' />
							<span
								role='button'
								tabIndex={0}
								className='absolute -top-2 -right-2 z-10 rounded-full bg-background/90 border border-border p-1 shadow-sm cursor-pointer hover:bg-accent'
								onClick={e => {
									e.stopPropagation();
									handleRemove();
								}}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										e.stopPropagation();
										handleRemove();
									}
								}}
							>
								<XIcon size={14} />
							</span>
						</>
					) : uploading ? (
						<Spinner className='size-6 text-muted-foreground' />
					) : (
						<>
							<ImagePlusIcon size={24} className='text-muted-foreground' />
							<span className='text-xs text-muted-foreground'>Загрузить иконку</span>
						</>
					)}
				</button>
			</div>

			{loading ? (
				<div className='flex justify-center py-4'>
					<Spinner className='size-5 text-muted-foreground' />
				</div>
			) : (
				<>
					{userIcons.length > 0 ? (
						<>
							<Separator />
							<IconGridSection title='Ваши иконки' count={userIcons.length}>
								<IconGrid
									icons={userIcons}
									selectedUrl={value}
									disabled={disabled || uploading}
									initialVisibleCount={5}
									onSelect={handleSelect}
								/>
							</IconGridSection>
						</>
					) : null}

					<Separator />

					<IconGridSection
						title='Готовые иконки'
						count={defaultIcons.length}
						emptyText={defaultIcons.length === 0 ? 'Администратор ещё не добавил иконки' : undefined}
					>
						{defaultIcons.length > 0 ? (
							<IconGrid
								icons={defaultIcons}
								selectedUrl={value}
								disabled={disabled || uploading}
								initialVisibleCount={5}
								onSelect={handleSelect}
							/>
						) : null}
					</IconGridSection>
				</>
			)}

			<input
				type='file'
				ref={fileRef}
				onChange={handleFileChange}
				accept='image/*'
				className='sr-only'
				disabled={disabled || uploading}
			/>
		</div>
	);
}
